import { TFunction } from 'i18next';
import {
  SendMessageRecipient,
  SendMessageType,
  SimplifiedWorkflowStepFormData,
  SimplifiedWorkflowTriggerStepFormData,
  StepType,
} from '../data/models/simplified-workflow.model';
import { uniqueId } from 'lodash';
import {
  CreatableWorkflow,
  Workflow,
  WorkflowState,
} from '../data/models/workflow.model';
import { WorkflowError } from './workflowUtils';
import { SimplifiedWorkflowFormTransformerRegistry } from './simplifiedWorkflowFormTransformers';
import { SimplifiedWorkflowModelTransformerRegistry } from './simplifiedWorkflowModelTransformers';
import { SmartLabel } from '../data/models/smart-label.model';
import { SystemEvent } from '../data/models/system-event.model';

export interface CreateSimplifiedWorkflowData {
  workQueueUUID: string;
  name: string;
  description?: string;
  state?: WorkflowState;
  workflowUUID?: string;
  steps: SimplifiedWorkflowStepFormData[];
}

class SimplifiedWorkflowUtils {
  public createWorkflowFromForm(
    data: CreateSimplifiedWorkflowData,
    smartLabels: SmartLabel[],
    systemEvents: SystemEvent[],
  ): CreatableWorkflow {
    const { workQueueUUID, name, description, steps } = data;
    if (!name) {
      throw new WorkflowError(
        'Workflow name is missing',
        'workflows.nameMissingError',
      );
    }

    const registry = new SimplifiedWorkflowFormTransformerRegistry();
    const transformer = registry.getTransformer(steps);
    const modelSteps = transformer.transform(steps, smartLabels, systemEvents);

    const creatableWorkflow: CreatableWorkflow = {
      workQueueUUID,
      name,
      description,
      steps: modelSteps,
    };
    return creatableWorkflow;
  }

  public createWorkflowFormFromModel(
    workflow: Workflow,
    translateFunction: TFunction,
    smartLabels: SmartLabel[],
  ): CreateSimplifiedWorkflowData {
    const registry = new SimplifiedWorkflowModelTransformerRegistry();
    const transformer = registry.getTransformer(workflow.steps);
    const modelSteps = transformer?.transform(
      workflow.steps,
      translateFunction,
      smartLabels,
    );

    return {
      workQueueUUID: workflow.workQueueUUID,
      workflowUUID: workflow.workflowUUID,
      name: workflow.name,
      description: workflow.description,
      state: workflow.state,
      steps: modelSteps ?? [],
    };
  }

  public replaceSmartLabelsWithBC(message: string, smartLabels: SmartLabel[]) {
    let updatedMessage = message;
    smartLabels.forEach((smartLabel) => {
      updatedMessage = updatedMessage.replaceAll(
        `${smartLabel.smartLabelName}`,
        `${smartLabel.smartLabelBC}`,
      );
    });
    return updatedMessage;
  }

  public replaceSmartLabelBCWithName(
    message: string,
    smartLabels: SmartLabel[],
  ) {
    let updatedMessage = message;
    smartLabels.forEach((smartLabel) => {
      updatedMessage = updatedMessage.replaceAll(
        `${smartLabel.smartLabelBC}`,
        `${smartLabel.smartLabelName}`,
      );
    });
    return updatedMessage;
  }

  public getDefaultTriggerStep(
    translateFunction: TFunction,
  ): SimplifiedWorkflowTriggerStepFormData {
    return {
      title: translateFunction('workflows.trigger'),
      type: StepType.Trigger,
      tempId: uniqueId(),
    };
  }

  public getDefaultActionStep(
    translateFunction: TFunction,
    parentId?: string,
    branch?: string,
  ): SimplifiedWorkflowTriggerStepFormData {
    return {
      title: translateFunction('workflows.action'),
      type: StepType.Action,
      tempId: uniqueId(),
      parentId,
      branch,
    };
  }

  public getTranslationKeyForSendMessageType(sendMessageType: SendMessageType) {
    switch (sendMessageType) {
      case SendMessageType.Email:
        return 'email';
      case SendMessageType.SMS:
        return 'sms';
      case SendMessageType.Voice:
        return 'voice';
    }
  }

  public getTranslationKeyForSendMessageRecipient(
    sendMessageRecipient: SendMessageRecipient,
  ) {
    switch (sendMessageRecipient) {
      case SendMessageRecipient.Internal:
        return 'internal';
      case SendMessageRecipient.Member:
        return 'member';
      case SendMessageRecipient.Provider:
        return 'provider';
    }
  }

  public getVersionedSystemEvents(
    systemEventNames: string[],
    systemEvents: SystemEvent[],
  ) {
    return systemEventNames.map((systemEventName) =>
      this.getVersionedSystemEvent(systemEventName, systemEvents),
    );
  }

  public getVersionedSystemEvent(
    systemEventName: string,
    systemEvents: SystemEvent[],
  ) {
    return {
      name: systemEventName,
      version: this.getSystemEventMostRecentVersion(
        systemEventName,
        systemEvents,
      ),
    };
  }

  public getSystemEventMostRecentVersion(
    systemEventName: string,
    systemEvents: SystemEvent[],
  ) {
    const latestMap: { [key: string]: SystemEvent } = {};

    systemEvents.forEach((event) => {
      const existing = latestMap[event.name];
      if (!existing || event.version > existing.version) {
        latestMap[event.name] = event;
      }
    });

    return latestMap[systemEventName]?.version ?? 1;
  }
}

const simplifiedWorkflowUtils = new SimplifiedWorkflowUtils();

export default simplifiedWorkflowUtils;
